<template>
  <div :class="$style['incremental-table']">
    <mds-table fixed-first-col>
      <mds-thead>
        <mds-th
          v-for="(header, index) in headers"
          :key="index"
          :style="header.style"
          :right-aligned="index !== 0"
        >
          <div :class="$style['header-cell-container']">
            <span :class="$style['header-cell-text']" :title="header.name">{{ header.name }}</span>
            <mds-button
              v-if="header.fieldName.includes('breakdown')"
              :id="`breakdown-fee-${index}`"
              :class="$style['dropdown-button']"
              variation="icon-only"
              icon="caret-circle-down"
              size="small"
              type="button"
              @click="header.showActionMenu = !header.showActionMenu"
            ></mds-button>
          </div>
          <mds-popover
            v-model="header.showActionMenu"
            :triggered-by="`breakdown-fee-${index}`"
            :position="['bottom-center']"
            width="200px"
            @mds-popover-dismissed="header.isRenameMode = false"
          >
            <mds-form v-if="header.isRenameMode">
              <mds-input
                size="small"
                :label="translate('feeColumnName')"
                :value="newColumnName"
                required
                @hook:created="newColumnName = header.name.slice(0, -2)"
                @blur="newColumnName = $event.target.value"
              >
              </mds-input>
              <mds-button-container right-aligned>
                <mds-button
                  variation="primary"
                  type="button"
                  :disabled="newColumnName === ''"
                  @click="handleColumnRename(header.breakdownIndex)"
                >
                  {{ translate('rename') }}
                </mds-button>
              </mds-button-container>
            </mds-form>
            <div v-else>
              <strong>{{ translate('action') }}</strong>
              <mds-list-group :class="$style['incremental-dropdown-list']">
                <mds-list-group-item @click.native="header.isRenameMode = true">
                  {{ translate('rename') }}
                </mds-list-group-item>
                <mds-list-group-item @click.native="deleteFeeColumn(header)">
                  {{ translate('delete') }}
                </mds-list-group-item>
              </mds-list-group>
            </div>
          </mds-popover>
        </mds-th>
      </mds-thead>
      <mds-tbody>
        <mds-tr v-for="(row, rowIndex) in rows" :key="rowIndex">
          <mds-td
            v-for="(header, colIndex) in headers"
            :key="colIndex"
            :right-aligned="colIndex !== 0"
          >
            <!-- Sequence column -->
            <div v-if="header.fieldName === 'firstCol'" :class="$style['firstCol']">
              <template v-if="rowIndex === 0">
                {{ translate('first') }}<span :class="$style['red']">*</span>
              </template>
              <template v-else-if="rowIndex === rows.length - 1">{{ translate('over') }}</template>
              <template v-else>{{ translate('next') }}</template>
            </div>
            <div v-else-if="header.fieldName === 'value'">
              <HypoNumberInput
                right-aligned
                :error="rowIndex === 0 && showFirstRowValueError"
                :saved-value="row.value"
                :placeholder="translate('enterAValue')"
                :fraction-digits="decimals.value.min"
                :max-fraction-digits="decimals.value.max"
                :allow-negative="false"
                :readonly="rowIndex === rows.length - 1"
                @update-saved-value="handleEditValue($event, rowIndex)"
              />
            </div>
            <div v-else-if="header.fieldName === 'annualAssetBasedFee'">
              <HypoNumberInput
                right-aligned
                is-percent
                :saved-value="feeScheduleDetails[rowIndex].annualAssetBasedFee"
                :readonly="showBreakdown"
                :error="showAnnualFeeError(rowIndex)"
                :fraction-digits="decimals.fee.min"
                :max-fraction-digits="decimals.fee.max"
                @update-saved-value="feeScheduleDetails[rowIndex].annualAssetBasedFee = $event || 0"
              />
            </div>
            <div v-else-if="header.fieldName.includes('breakdown')">
              <HypoNumberInput
                right-aligned
                is-percent
                :saved-value="row[header.fieldName]"
                :fraction-digits="decimals.fee.min"
                :max-fraction-digits="decimals.fee.max"
                @update-saved-value="handleEditFee($event, rowIndex, header.breakdownIndex)"
              />
            </div>
          </mds-td>
        </mds-tr>
      </mds-tbody>
    </mds-table>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsForm from '@mds/form';
import MdsInput from '@mds/input';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import HypoNumberInput from 'hypo-shared-utils/src/components/HypoNumberInput';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import cloneDeep from 'lodash.clonedeep';
import { formatNum } from 'hypo-shared-utils';
import { calculateAnnualAssetBasedFee, calculateTotalValue } from '../../utils';
import { decimals } from '../../_config/constants';

export default {
  name: 'incremental-table',
  components: {
    MdsForm,
    MdsInput,
    MdsButton,
    MdsButtonContainer,
    MdsPopover,
    MdsListGroup,
    MdsListGroupItem,
    HypoNumberInput,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    showBreakdown: {
      type: Boolean,
    },
    propFeeScheduleDetails: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      feeScheduleDetails: [],
      headers: {},
      newColumnName: '',
      showFirstRowValueError: false,
    };
  },
  computed: {
    staticHeaders() {
      const valueText = `${this.translate('value')} ${this.currencySymbol}`;
      const annualAssetBasedFeeText = this.translate('annualAssetBasedFee');
      return [
        {
          name: '',
          fieldName: 'firstCol',
          style: {
            width: '80px',
          },
        },
        {
          name: valueText,
          fieldName: 'value',
          style: {
            width: '160px',
          },
        },
        {
          name: annualAssetBasedFeeText,
          fieldName: 'annualAssetBasedFee',
          style: {
            width: '160px',
          },
        },
      ];
    },
    rows() {
      const result = [];
      // Dynamically calculate Total Value
      let totalValue = 0;
      this.feeScheduleDetails.forEach((row, rowIndex) => {
        const rowData = { value: row.lowValue };
        if (rowIndex === this.feeScheduleDetails.length - 1) {
          rowData.value = totalValue;
        } else if (rowIndex === 0) {
          totalValue += row.lowValue;
        } else {
          totalValue += row.lowValue;
        }

        if (!row.breakdown) {
          rowData.annualAssetBasedFee = row.annualAssetBasedFee;
        } else {
          // Dynamically calculate annualAssetBasedFee
          rowData.annualAssetBasedFee = 0;
          row.breakdown.forEach((column, colIndex) => {
            rowData[`breakdown-${colIndex}`] = column.feeAmount;
            rowData.annualAssetBasedFee += column.feeAmount;
          });
        }
        result.push(rowData);
      });
      return result;
    },
    decimals() {
      return decimals;
    },
  },
  watch: {
    feeScheduleDetails: {
      handler() {
        this.generateHeaders();
        this.$emit('details-updated', this.feeScheduleDetails);
      },
      deep: true,
    },
    showBreakdown: {
      handler() {
        this.setAllFeesToZero();
        this.generateHeaders();
      },
    },
  },
  created() {
    this.applyPropFeeScheduleDetails();
    this.generateHeaders();
  },
  methods: {
    applyPropFeeScheduleDetails() {
      this.feeScheduleDetails = cloneDeep(this.propFeeScheduleDetails);
    },
    generateHeaders() {
      const nonStaticHeaders = [];
      const { breakdown = [] } = this.feeScheduleDetails[0];
      if (this.showBreakdown && breakdown.length > 0) {
        for (let i = 0; i < breakdown.length; i += 1) {
          nonStaticHeaders.push({
            breakdownIndex: i,
            name: `${breakdown[i].feeName} %`,
            fieldName: `breakdown-${i}`,
            style: {
              width: '160px',
            },
            showActionMenu: false,
            isRenameMode: false,
          });
        }
      }
      this.headers = [...this.staticHeaders, ...nonStaticHeaders];
    },
    handleEditValue(value, rowIndex) {
      if (rowIndex === 0) {
        this.showFirstRowValueError = value === null;
      }
      const details = this.feeScheduleDetails;
      details[rowIndex].lowValue = value;
      details[details.length - 1].lowValue = calculateTotalValue(details);
    },
    showAnnualFeeError(rowIndex) {
      const item = this.feeScheduleDetails[rowIndex];
      // Ignore checking lowValue for FIRST and OVER row
      if (rowIndex === 0 || rowIndex === this.feeScheduleDetails.length - 1) {
        return item.annualAssetBasedFee > 100;
      }
      return item.lowValue !== null && item.annualAssetBasedFee > 100;
    },
    handleEditFee(value, rowIndex, breakdownIndex) {
      const target = this.feeScheduleDetails[rowIndex];
      target.breakdown[breakdownIndex].feeAmount = value || 0;
      target.annualAssetBasedFee = calculateAnnualAssetBasedFee(target.breakdown);
    },
    addRow() {
      const lastNonOver = this.feeScheduleDetails[this.feeScheduleDetails.length - 2];
      const sequence = (lastNonOver && lastNonOver.sequence) || 0;
      const { breakdown = [] } = this.feeScheduleDetails[0];
      const newBreakDown = breakdown.map(i => {
        return {
          id: i.id,
          feeName: i.feeName,
          feeAmount: 0,
        };
      });
      this.feeScheduleDetails.splice(this.feeScheduleDetails.length - 1, 0, {
        sequence: sequence + 1,
        lowValue: null,
        annualAssetBasedFee: 0,
        breakdown: newBreakDown,
      });
    },
    addFeeColumn(columnName) {
      const firstDetailBreakdown = this.feeScheduleDetails[0].breakdown;
      const { id } = firstDetailBreakdown[firstDetailBreakdown.length - 1] || { id: 0 };
      const newBreakdownItem = { id: id + 1, feeName: columnName, feeAmount: 0 };
      this.feeScheduleDetails.forEach(detailItem => {
        detailItem.breakdown.push(cloneDeep(newBreakdownItem));
      });
    },
    deleteFeeColumn(header) {
      this.feeScheduleDetails.forEach(detailItem => {
        detailItem.breakdown.splice(header.breakdownIndex, 1);
        detailItem.annualAssetBasedFee = calculateAnnualAssetBasedFee(detailItem.breakdown);
      });
    },
    handleColumnRename(breakdownIndex) {
      this.feeScheduleDetails.forEach(detailItem => {
        detailItem.breakdown[breakdownIndex].feeName = this.newColumnName;
      });
    },
    formatNum(num, minDecimal, maxDecimal) {
      return formatNum(num, minDecimal, maxDecimal);
    },
    setAllFeesToZero() {
      this.feeScheduleDetails.forEach(detailItem => {
        detailItem.annualAssetBasedFee = 0;
        detailItem.breakdown.forEach(fee => {
          fee.feeAmount = 0;
        });
      });
    },
    validateInput() {
      this.showFirstRowValueError = this.feeScheduleDetails[0].lowValue === null;
      this.feeScheduleDetails.forEach((item, index) => {
        const firstRowValidity = index === 0 ? !this.showFirstRowValueError : undefined;
        item.validity = {
          ...item.validity,
          firstRowValidity,
          annualAssetBasedFee: !this.showAnnualFeeError(index),
        };
      });
      this.$emit('details-updated', this.feeScheduleDetails);
    },
  },
};
</script>

<style lang="scss" module>
@import '@mds/typography';

.incremental-dropdown-list {
  margin-top: $mds-space-1-and-a-half-x;
  .mds-list-group-item__link-inner.mds-list-group-item__link-inner {
    padding-left: 0;
  }
}

.incremental-table {
  margin-top: $mds-space-1-x;
  .header-cell-container {
    display: flex;
    .dropdown-button {
      margin-left: $mds-space-three-quarter-x;
      box-shadow: none;
    }
    .header-cell-text {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .firstCol {
    font-size: 12px;
    font-weight: 700;
    .red {
      color: $mds-feedback-color-error;
    }
  }
}
</style>
